import React, { useState } from "react";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, QrCode, Loader2 } from "lucide-react";
import { QrLinksPanel, goUrl } from "./QrLinksPanel";

const PRESETS = [
  { value: "card", label: "Nyomtatott kártya" },
  { value: "flyer", label: "Szórólap" },
  { value: "partner", label: "Partner" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
];

const COUNTS = [
  { value: "1", label: "1 kártya / A6 oldal" },
  { value: "2", label: "2 kártya / A4 oldal" },
  { value: "4", label: "4 kártya / A4 oldal" },
];

export default function AdminQrCards() {
  const defaultTarget = window.location.origin + "/";
  const [form, setForm] = useState({
    target_url: defaultTarget,
    headline: "Ne dobd ki. 🌱",
    subtitle: "Valami új készül a XVII.-ben.",
    utm_source: "card",
    utm_medium: "qr",
    utm_campaign: "elso_100",
    count: "4",
  });
  const [loading, setLoading] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const qrTarget = selectedSlug ? goUrl(selectedSlug) : form.target_url;

  const download = async () => {
    if (!form.target_url) return toast.error("Add meg a cél URL-t");
    setLoading(true);
    try {
      const token = localStorage.getItem("gb_admin_token");
      const res = await fetch(`${API}/admin/qr-card`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, target_url: qrTarget, count: parseInt(form.count, 10) }),
      });
      if (!res.ok) throw new Error("Hiba a PDF generálásakor");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `growbag-kampanykartya-${form.utm_campaign || "card"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF letöltve.");
    } catch (e) {
      toast.error(e.message || "Nem sikerült generálni a PDF-et");
    } finally {
      setLoading(false);
    }
  };

  const previewUrl = (() => {
    if (selectedSlug) return goUrl(selectedSlug);
    const u = form.target_url || "";
    const sep = u.includes("?") ? "&" : "?";
    return `${u}${sep}utm_source=${form.utm_source}&utm_medium=${form.utm_medium}&utm_campaign=${form.utm_campaign}`;
  })();

  return (
    <div className="space-y-8">
      <div>
        <p className="section-label">QR kampánykártya</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#201B18] mt-1">PDF generátor</h1>
        <p className="text-sm text-[#5C524A] mt-2 max-w-xl">
          Készíts nyomtatható kampánykártyákat egyedi QR-kóddal. A QR a megadott landing oldalra mutat UTM paraméterekkel – így az admin dashboardon látod, melyik forrás hozott feliratkozókat.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-[#2C221E]/10 p-6 space-y-5">
          <div className="space-y-2">
            <Label>Cél URL</Label>
            <Input value={form.target_url} onChange={(e) => update("target_url", e.target.value)} data-testid="qr-target-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Kampányforrás (utm_source)</Label>
              <Select value={form.utm_source} onValueChange={(v) => update("utm_source", v)}>
                <SelectTrigger data-testid="qr-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Kampány név (utm_campaign)</Label>
              <Input value={form.utm_campaign} onChange={(e) => update("utm_campaign", e.target.value)} data-testid="qr-campaign-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Főcím (max 40 karakter)</Label>
            <Input value={form.headline} onChange={(e) => update("headline", e.target.value)} maxLength={40} data-testid="qr-headline-input" />
          </div>
          <div className="space-y-2">
            <Label>Alcím (max 60 karakter)</Label>
            <Textarea value={form.subtitle} onChange={(e) => update("subtitle", e.target.value)} rows={2} maxLength={60} data-testid="qr-subtitle-input" />
          </div>
          <div className="space-y-2">
            <Label>Elrendezés</Label>
            <Select value={form.count} onValueChange={(v) => update("count", v)}>
              <SelectTrigger data-testid="qr-count-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="pt-2 border-t border-[#2C221E]/10">
            <p className="section-label mb-2">{selectedSlug ? "A QR állandó linkje (kiválasztva)" : "A QR cél linkje (közvetlen)"}</p>
            <p className="font-mono-brand text-[11px] text-[#5C524A] break-all" data-testid="qr-preview-url">{previewUrl}</p>
          </div>
          <QrLinksPanel form={form} selectedSlug={selectedSlug} onSelect={setSelectedSlug} />
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl bg-[#F3EDE2] border border-[#C29B78]/30 p-6 text-center">
            <QrCode className="h-14 w-14 mx-auto text-[#2C221E] mb-3" />
            <p className="font-mono-brand text-[11px] tracking-[0.25em] text-[#8C8075] uppercase mb-1">A6 / A4 PDF</p>
            <p className="text-sm text-[#5C524A]">Nyomtatóbarát, kraft stílusú kampánykártya a GrowBag arculattal.</p>
          </div>
          <Button onClick={download} disabled={loading} data-testid="qr-download-button" className="w-full h-12 bg-[#2C221E] hover:bg-[#1a1310] text-[#FBF8F3] rounded-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" /> PDF letöltése</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
